/** SPEC §19: eigen velden voor project, werkpakket en taak. */
export const VELD_ENTITEITEN = ['PROJECT', 'WERKPAKKET', 'TAAK'] as const;
export type VeldEntiteit = (typeof VELD_ENTITEITEN)[number];
export const VELD_SOORTEN = ['TEKST', 'LANGE_TEKST', 'GETAL', 'DATUM', 'KEUZE', 'JA_NEE', 'LINK'] as const;
export type VeldSoort = (typeof VELD_SOORTEN)[number];
export const VELD_SOORT_LABEL: Record<VeldSoort, string> = {
  TEKST: 'Tekst', LANGE_TEKST: 'Lange tekst', GETAL: 'Getal', DATUM: 'Datum', KEUZE: 'Keuzelijst', JA_NEE: 'Ja/nee', LINK: 'Link',
};
export const VELD_ENTITEIT_LABEL: Record<VeldEntiteit, string> = { PROJECT: 'Project', WERKPAKKET: 'Werkpakket', TAAK: 'Taak' };
export const MAX_VELDEN = 30;

export interface EigenVeld {
  id: string;
  entiteit: VeldEntiteit | string;
  naam: string;
  sleutel: string;
  soort: VeldSoort | string;
  keuzes: string[];
  verplicht: boolean;
  kolom: boolean;
  volgorde: number;
}

/** "Kosten­plaats (NL)" → "kostenplaats_nl" */
export function veldSleutel(naam: string): string {
  return (
    naam
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'veld'
  );
}

const leeg = (v: unknown) => v == null || v === '' || (Array.isArray(v) && !v.length);

/** Eén waarde controleren en omzetten; geeft [waarde, fout]. */
export function veldWaarde(def: EigenVeld, v: unknown): [unknown, string | null] {
  if (leeg(v)) return [null, def.verplicht ? `${def.naam} is verplicht` : null];
  switch (def.soort) {
    case 'TEKST':
    case 'LANGE_TEKST': {
      const t = String(v).trim();
      const max = def.soort === 'TEKST' ? 500 : 5000;
      if (!t) return [null, def.verplicht ? `${def.naam} is verplicht` : null];
      return t.length > max ? [null, `${def.naam} is te lang (max. ${max} tekens)`] : [t, null];
    }
    case 'GETAL': {
      const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
      return Number.isFinite(n) ? [n, null] : [null, `${def.naam} moet een getal zijn`];
    }
    case 'DATUM':
      return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? [v, null] : [null, `${def.naam} moet een datum zijn (jjjj-mm-dd)`];
    case 'KEUZE':
      return def.keuzes.includes(String(v)) ? [String(v), null] : [null, `${def.naam}: kies een van ${def.keuzes.join(', ')}`];
    case 'JA_NEE':
      if (typeof v === 'boolean') return [v, null];
      if (v === 'true' || v === 'ja') return [true, null];
      if (v === 'false' || v === 'nee') return [false, null];
      return [null, `${def.naam} moet ja of nee zijn`];
    case 'LINK': {
      const t = String(v).trim();
      return /^https?:\/\/[^\s]+$/i.test(t) && t.length <= 1000 ? [t, null] : [null, `${def.naam} moet een link zijn die begint met http:// of https://`];
    }
    default:
      return [null, null];
  }
}

/**
 * Waarden controleren tegen de definities. `invoer` kan op veld-id of op sleutel zijn.
 * Zonder invoer (undefined) blijven de oude waarden staan; verplichte velden worden dan niet afgedwongen.
 */
export function valideerVelden(defs: EigenVeld[], invoer: Record<string, unknown> | undefined | null, oud?: Record<string, unknown> | null): { waarden: Record<string, unknown>; fouten: string[] } {
  if (invoer == null) return { waarden: { ...(oud ?? {}) }, fouten: [] };
  const waarden: Record<string, unknown> = {};
  const fouten: string[] = [];
  for (const def of defs) {
    const heeft = def.id in invoer || def.sleutel in invoer;
    const ruw = def.id in invoer ? invoer[def.id] : def.sleutel in invoer ? invoer[def.sleutel] : oud?.[def.id];
    if (!heeft && oud == null && !def.verplicht) continue;
    const [w, f] = veldWaarde(def, ruw);
    if (f) fouten.push(f);
    else if (w != null) waarden[def.id] = w;
  }
  return { waarden, fouten };
}

/** Op sleutel (voor de openbare API en webhooks). */
export function veldenOpSleutel(defs: EigenVeld[], waarden: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const uit: Record<string, unknown> = {};
  for (const d of defs) uit[d.sleutel] = waarden?.[d.id] ?? null;
  return uit;
}

/** Waarde als tekst voor lijsten. */
export function toonVeld(def: EigenVeld, v: unknown): string {
  if (v == null || v === '') return '';
  if (def.soort === 'JA_NEE') return v ? 'ja' : 'nee';
  if (def.soort === 'DATUM' && typeof v === 'string') {
    const [j, m, d] = v.split('-');
    return `${Number(d)}-${Number(m)}-${j}`;
  }
  if (def.soort === 'GETAL' && typeof v === 'number') return v.toLocaleString('nl-NL');
  return String(v);
}
